import { MouseEvent, ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';

import {
  changeCurrentLine,
  changeDealData,
  createNewLine,
  duplicateLine,
  removeTemporaryDealId,
} from 'store/dealManagement/reducer';

import plusIcon from 'assets/icons/plus.svg';
import copyIcon from 'assets/icons/copy.svg';
import arrowDownSvg from 'assets/icons/arrow_down.svg';
import downUpSvg from 'assets/icons/arrow_up.svg';

import { getFormattedDateTime } from 'modules/I18N';
import { getAsDate } from 'utils/getAsDate';
import { DealStatus, Line } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { DirectSalesLine } from 'components/common/types/DirectSalesCampaign.types';
import { getActionConditions } from 'store/dealManagement/selectors';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import DealLine from './DealLine';

type SortOrder = 'asc' | 'desc';

type LineOrDirectSalesLine = Line | DirectSalesLine;

interface DealLinesProps {
  handleMouseEnter?: (lineId: string) => void;
  closePopup: () => void;
}

const sortLines = (lines: LineOrDirectSalesLine[], sortOrder: SortOrder): LineOrDirectSalesLine[] =>
  [...lines].sort((a, b) => {
    const firstValue = sortOrder === 'asc' ? a : b;
    const secondValue = sortOrder === 'asc' ? b : a;
    return getAsDate(firstValue.createdDate).getTime() - getAsDate(secondValue.createdDate).getTime();
  });

const DealLines = ({ handleMouseEnter = () => null, closePopup }: DealLinesProps): ReactElement => {
  const dispatch = useDispatch();
  const dealManagementState = useSelector((state: Store) => state.dealManagement);
  const {
    backupFormData: {
      lines,
      commonDeal: { summary: backupDealSummary },
    },
    commonDeal: { currentLine, bookingStatusCode },
  } = dealManagementState;
  const hasOutOfChargeEnabled = useHasFeatureAccess(FeatureFlags.OUT_OF_CHARGE);

  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const actionConditions = useSelector(getActionConditions);

  const handleCreateLine = (): void => {
    if (actionConditions.createLine.disabled) return;
    dispatch(createNewLine());
    closePopup();
  };

  const handleLineSorting = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setSortOrder((prevState) => (prevState === 'asc' ? 'desc' : 'asc'));
  };

  const handleDuplicateLine = (lineId: string): void => {
    if (actionConditions.duplicateLine.disabled) return;
    dispatch(duplicateLine(lineId));
    closePopup();
  };

  const handleSelectLine = (lineId: string): void => {
    dispatch(changeCurrentLine(lineId));
    dispatch(removeTemporaryDealId());
    dispatch(changeDealData({ summary: backupDealSummary }));
    closePopup();
  };

  const isBookingStatusFailed = bookingStatusCode === DealStatus.FAILED;

  return (
    <div className="divide-y-2 divide-neutral-300 border-r-2 border-neutral-300">
      <div className="bg-neutral-100 px-4 py-7 space-y-6 text-primary">
        <div className="flex justify-between items-center space-x-1.5">
          <p className="sub-header-lg">{currentLine.id ? currentLine.name : 'New line'}</p>
          <p className="body-base">
            {getFormattedDateTime(new Date(currentLine.createdDate), {
              dateStyle: 'medium',
            })}
          </p>
        </div>
        <div className="flex justify-around">
          {actionConditions.createLine.show ? (
            <button
              type="button"
              data-test-id="panel-create-line-button"
              onClick={handleCreateLine}
              disabled={actionConditions.createLine.disabled}
              className="disabled:opacity-25 disabled:text-neutral-900"
            >
              <SVG src={plusIcon} />
            </button>
          ) : null}
          {actionConditions.duplicateLine.show ? (
            <button
              type="button"
              data-test-id="panel-duplicate-line-button"
              onClick={() => handleDuplicateLine(currentLine.id)}
              disabled={actionConditions.duplicateLine.disabled}
              className="disabled:opacity-25 disabled:text-neutral-900"
            >
              <SVG src={copyIcon} />
            </button>
          ) : null}
          <button type="button" data-test-id="panel-sort-lines-button" onClick={handleLineSorting}>
            <SVG src={sortOrder === 'asc' ? arrowDownSvg : downUpSvg} />
          </button>
        </div>
      </div>

      <div className="p-2 pb-0 max-h-80 overflow-y-scroll">
        {sortLines(lines, sortOrder).map((line) => {
          return (
            <DealLine
              key={line.id}
              line={line}
              bookingStatusCode={bookingStatusCode}
              isBookingStatusFailed={isBookingStatusFailed}
              isMultiLineDeal={lines.length > 1}
              isLineDuplicationDisabled={actionConditions.duplicateLine.disabled}
              hasOutOfChargeEnabled={hasOutOfChargeEnabled}
              onMouseEnter={handleMouseEnter}
              onSelectLine={handleSelectLine}
              onDuplicateLine={handleDuplicateLine}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DealLines;
